export type DateTimeValue = string | number | Date | null | undefined;

const isValidDate = (value: Date): boolean => !Number.isNaN(value.getTime());

const createLocalDate = (
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0
): Date | null => {
  const value = new Date(year, month - 1, day, hour, minute, second);
  return value.getFullYear() === year
    && value.getMonth() === month - 1
    && value.getDate() === day
    && value.getHours() === hour
    && value.getMinutes() === minute
    && value.getSeconds() === second
    ? value
    : null;
};

const parseNumericTimestamp = (value: number): Date | null => {
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  // RTU files use both Unix seconds and Unix milliseconds.
  const milliseconds = Math.abs(value) < 100_000_000_000 ? value * 1000 : value;
  const parsed = new Date(milliseconds);
  return isValidDate(parsed) ? parsed : null;
};

const parseRtuLocalDateTime = (value: string): Date | null => {
  const reportDate = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s*-\s*(\d{1,2}):(\d{2}):(\d{2})$/
  );
  if (reportDate) {
    const [, month, day, rawYear, hour, minute, second] = reportDate;
    const year = rawYear.length === 2 ? 2000 + Number(rawYear) : Number(rawYear);
    return createLocalDate(year, Number(month), Number(day), Number(hour), Number(minute), Number(second));
  }

  const installationDate = value.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i
  );
  if (installationDate) {
    const [, year, month, day, rawHour, minute, rawSecond = '0', meridiem] = installationDate;
    let hour = Number(rawHour);
    if (meridiem) {
      hour %= 12;
      if (meridiem.toLowerCase() === 'pm') {
        hour += 12;
      }
    }
    return createLocalDate(Number(year), Number(month), Number(day), hour, Number(minute), Number(rawSecond));
  }

  return null;
};

export const parseDateTimeValue = (value: DateTimeValue): Date | null => {
  if (value instanceof Date) {
    // Never surface the Unix epoch (or earlier) as a real timestamp - it only
    // ever means "missing/invalid" upstream, regardless of the input shape
    // (numeric/string paths already guard this; this covers a literal Date,
    // e.g. one built from an ISO "1970-01-01T00:00:00Z" string elsewhere).
    return isValidDate(value) && value.getTime() > 0 ? value : null;
  }
  if (typeof value === 'number') {
    return parseNumericTimestamp(value);
  }
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^[+-]?\d+(?:\.\d+)?$/.test(trimmed)) {
    return parseNumericTimestamp(Number(trimmed));
  }

  const rtuDate = parseRtuLocalDateTime(trimmed);
  if (rtuDate) {
    return rtuDate;
  }

  const parsed = new Date(trimmed);
  return isValidDate(parsed) && parsed.getTime() > 0 ? parsed : null;
};

export const formatDisplayDateTime = (
  value: DateTimeValue,
  fallback = '—'
): string => {
  const parsed = parseDateTimeValue(value);
  if (!parsed) {
    return fallback;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};
